// Single source of truth for plan prices shown in the UI. Keep in sync with backend/pricing.py
// (backend/tests/test_pricing.py checks that both files agree).
export type PricedPlanKey = 'starter' | 'professional' | 'scale';
export type BillingTerm = 'yearly' | 'two_year' | 'three_year';

export const PLAN_ANNUAL_USD: Record<PricedPlanKey, number> = {
  starter: 1999,
  professional: 3999,
  scale: 6999,
};

export const BILLING_TERMS: { key: BillingTerm; years: number; discount: number }[] = [
  { key: 'yearly', years: 1, discount: 0 },
  { key: 'two_year', years: 2, discount: 0.17 },
  { key: 'three_year', years: 3, discount: 0.3 },
];

export const DEFAULT_BILLING_TERM: BillingTerm = 'yearly';

export const isBillingTerm = (value: unknown): value is BillingTerm =>
  BILLING_TERMS.some((term) => term.key === value);

export const isPricedPlan = (value: unknown): value is PricedPlanKey =>
  typeof value === 'string' && value in PLAN_ANNUAL_USD;

const round2 = (value: number) => Math.round(value * 100) / 100;

const termOf = (term: BillingTerm) => BILLING_TERMS.find((item) => item.key === term) || BILLING_TERMS[0];

export const termYears = (term: BillingTerm) => termOf(term).years;
export const termDiscountPercent = (term: BillingTerm) => Math.round(termOf(term).discount * 100);

/** What the customer pays up front for the whole term. */
export const termTotalUsd = (plan: PricedPlanKey, term: BillingTerm) => {
  const { years, discount } = termOf(term);
  return round2(PLAN_ANNUAL_USD[plan] * years * (1 - discount));
};

/** Full price of the same term without discount. */
export const termListUsd = (plan: PricedPlanKey, term: BillingTerm) => PLAN_ANNUAL_USD[plan] * termOf(term).years;

export const termPerYearUsd = (plan: PricedPlanKey, term: BillingTerm) => round2(termTotalUsd(plan, term) / termOf(term).years);

export const termPerMonthUsd = (plan: PricedPlanKey, term: BillingTerm) =>
  round2(termTotalUsd(plan, term) / (termOf(term).years * 12));

export const formatUsd = (amount: number) =>
  `$${amount.toLocaleString(undefined, {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
